import { rufe } from './client';

/**
 * Storno, Umbuchung und Teilnehmerwechsel (BUC-45, BUC-142, BUC-51).
 *
 * ============================================================================
 * ALLE BETRÄGE SIND ZEICHENKETTEN
 * ============================================================================
 *
 * `4000.10` ist als JSON-Zahl nicht exakt darstellbar, und über eine
 * Gleitkommazahl liefe hier der Betrag, an dem die Gebühr hängt (RND-01). Der
 * Server schickt sie mit zwei Nachkommastellen, die Maske zeigt sie, und
 * gerechnet wird an keiner Stelle im Browser.
 */

export type Vorgangsart = 'storno' | 'umbuchung' | 'teilnehmerwechsel';

/** AeA-03: ueber welchen Weg gebucht wurde. */
export type Buchungsweg = 'portal' | 'innendienst' | 'angebot';

export const VORGANGSNAME: Readonly<Record<Vorgangsart, string>> = {
  storno: 'Storno',
  umbuchung: 'Umbuchung',
  teilnehmerwechsel: 'Teilnehmerwechsel',
};

export type Buchungsauskunft = {
  readonly id: string;
  readonly buchungsnummer: string;
  readonly plaetze: number;
  readonly status: string;
  readonly vertragsart: string;
  readonly flextarifVerbrauchtAm: string | null;
  readonly auftragsposition: string | null;
  /** BUC-121: der Auftrag zur Buchung — die Belegseite. */
  readonly auftrag: string | null;
  readonly auftragsnummer: string | null;
  readonly auftragsstatus: string | null;
  /** Wie viele Teilnehmerzeilen schon auf dem Auftrag stehen. 0 = kein Beleg. */
  readonly belegzeilen: number;
  /** DAT-11, WEB-12: der jüngste Zahlungsvorgang — oder keiner. */
  readonly zahlung: {
    readonly id: string;
    readonly status: 'angelegt' | 'bestaetigt' | 'fehlgeschlagen' | 'abgebrochen';
    readonly betrag: string;
    readonly art: string;
    readonly dienstleister: string | null;
    readonly erzeugtAm: string;
    readonly bestaetigtAm: string | null;
  } | null;
  readonly teilnehmerFrist: string | null;
  readonly kunde: string;
  readonly kundenname: string;
  readonly kundenart: string;
  readonly termin: string;
  readonly beginn: string;
  readonly ende: string;
  readonly artikel: string;
  readonly offeneTeilnehmer: number;
  /** BUC-106: die EINGEFRORENE Kundenart — nicht die des Kunden von heute. */
  readonly buchungskundenart: string | null;
  /** BUC-102: folgt aus der Kundenart. Privat = brutto, sonst netto. */
  readonly preisfuehrung: string | null;
  readonly bestellreferenz: string | null;
  readonly kostenstelle: string | null;
  readonly rechnungsempfaenger: string | null;
  readonly rechnungsempfaengername: string | null;
  /** BUC-79: portal, telefon, innendienst, angebot. */
  readonly herkunft?: string;
  /** BUC-43: was der Lauf mit der Webbuchung getan hat — `null` bei jeder anderen. */
  readonly faktura: Fakturavermerk | null;
};

/** BUC-43: der Stand der automatischen Rechnung zu einer Webbuchung. */
export type Fakturavermerk = {
  readonly stand: 'ausgenommen' | 'auftrag' | 'entwurf' | 'festgeschrieben' | 'fehler';
  readonly meldung: string | null;
  readonly versuche: number;
  readonly rechnungsnummer: string | null;
};

/**
 * Was der Vorgang kosten würde (BUC-45).
 *
 * `nichtVorgesehen` ist KEIN Fehler und keine Gebühr von null: Die
 * Vorgangsart ist in diesem Zeitfenster ausgeschlossen (Tabelle 28). FRS-15
 * verlangt, die Handlung dann gar nicht anzubieten — die Maske schaltet den
 * Knopf ab, statt ihn anzubieten und danach abzulehnen.
 */
export type Vorschau = {
  readonly art: Vorgangsart;
  readonly staffel: string;
  readonly staffelBezeichnung: string;
  readonly satzProzent: string | null;
  readonly stufeAbTagen: number | null;
  readonly abstandInTagen: number;
  readonly grundbetrag: string;
  readonly gebuehr: string;
  readonly erstattung: string;
  readonly verrechnet: string;
  readonly zuErstatten: string;
  readonly zuBerechnen: string;
  readonly nichtVorgesehen: boolean;
  readonly flextarifGenutzt: boolean;
  readonly hinweise: readonly string[];
};

export type Vorgangszeile = {
  readonly id: string;
  readonly art: Vorgangsart;
  readonly plaetze: number;
  readonly zugangAm: string;
  readonly erfasstAm: string;
  readonly abstandInTagen: number;
  readonly staffelKennung: string | null;
  readonly satzProzent: string | null;
  readonly grundbetrag: string;
  readonly gebuehr: string;
  readonly erstattung: string;
  readonly verrechnet: string;
  readonly zuErstatten: string;
  readonly zuBerechnen: string;
  readonly herabgesetztAuf: string | null;
  readonly zurueckgenommenAm: string | null;
  readonly herabsetzungGrund: string | null;
  readonly flextarifGenutzt: boolean;
  readonly grund: string | null;
  readonly beleg: string | null;
  readonly belegErstelltAm: string | null;
  readonly belegart: string | null;
  readonly rechnungsnummer: string | null;
  readonly belegstatus: string | null;
};

export type Vorgangseingabe = {
  readonly zugangAm: string;
  readonly grundbetrag?: string;
  readonly plaetze?: number;
  readonly zielTermin?: string;
  readonly grund?: string;
  /** C3.4, C3.7: wer beim Teilstorno geht oder bei der Teilumbuchung umzieht. */
  readonly teilnehmer?: readonly string[];
  /** BUC-144 (AeA-59): wie die Erklärung einging, mit freiem Verweis; nur beim Storno. */
  readonly erklaerungsweg?: string;
  readonly erklaerungsverweis?: string;
};

export async function ueberNummer(nummer: string): Promise<Buchungsauskunft> {
  return rufe<Buchungsauskunft>(`/buchungen?nummer=${encodeURIComponent(nummer)}`);
}

/** Ein Treffer der Buchungssuche (BUC-80). */
export type Buchungstreffer = {
  readonly id: string;
  readonly buchungsnummer: string;
  readonly status: string;
  readonly plaetze: number;
  readonly kundennummer: string;
  readonly kundenname: string;
  readonly produkt: string;
  readonly beginn: string;
  readonly auftragsnummer: string | null;
  readonly teilnehmer: readonly string[];
  /** BUC-122 (Auftrag CRM-140): die Gesamtsumme, wie sie die Buchungsakte nennt. */
  readonly betrag: string;
};

export type Buchungssuchfelder = {
  readonly nummer?: string;
  readonly name?: string;
  readonly produkt?: string;
  readonly von?: string;
  readonly bis?: string;
  readonly status?: string;
};

/**
 * Buchungen über benannte Felder suchen (BUC-80, M-07).
 *
 * Leere Felder werden WEGGELASSEN — ein mitgeschicktes leeres Feld wäre für
 * den Leser des Protokolls ein gesetztes.
 */
export async function suchen(felder: Buchungssuchfelder): Promise<{
  readonly treffer: readonly Buchungstreffer[];
  readonly mehrAlsGezeigt: boolean;
  readonly hoechstens: number;
}> {
  return rufe(`/buchungen/suche?${gesetzteFelder(felder).toString()}`);
}

function gesetzteFelder(felder: Buchungssuchfelder): URLSearchParams {
  const p = new URLSearchParams();
  for (const [schluessel, wert] of Object.entries(felder)) {
    if (typeof wert === 'string' && wert.trim() !== '') p.set(schluessel, wert.trim());
  }
  return p;
}

/** Eine Seite der Buchungsliste (Auftrag CRM-70). */
export type Buchungsliste = {
  readonly buchungen: readonly Buchungstreffer[];
  readonly mehr: boolean;
  /** Ohne Zeitraum zeigt der Server die Termine ab heute — die Maske sagt es dazu. */
  readonly abHeute: boolean;
  readonly seitengroesse: number;
};

/**
 * Die Buchungen als Liste, der Einstieg der Maske (Auftrag CRM-70).
 *
 * Anders als `suchen` auch ohne ein Feld. Reihenfolge und Untergrenze legt
 * der Server fest, nicht die Maske.
 */
export async function buchungsliste(felder: Buchungssuchfelder, versatz = 0): Promise<Buchungsliste> {
  const p = gesetzteFelder(felder);
  if (versatz > 0) p.set('versatz', String(versatz));
  return rufe(`/buchungen/liste?${p.toString()}`);
}

/** Was eine Buchung kosten wuerde (BUC-80, BUC-102, BUC-120). */
export type Preisauskunft = {
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly bezeichnung: string;
  readonly plaetze: number;
  readonly kundenart: string;
  /** Welche Zahl die fuehrende ist — brutto beim Verbraucher (BUC-120). */
  readonly preisfuehrung: 'brutto' | 'netto';
  readonly stichtag: string;
  readonly preis: {
    readonly preisliste: string;
    readonly festpreisAus: string | null;
    readonly einzelpreis: string;
    readonly staffelpreis: string | null;
    readonly abMenge: number | null;
    readonly steuerschluessel: string;
    readonly steuerProzent: string;
    readonly netto: string;
    readonly steuerbetrag: string;
    readonly brutto: string;
    readonly istBrutto: boolean;
    readonly darstellbar: boolean;
    readonly vorteil: string;
    readonly vorteilProzent: string;
  } | null;
};

/**
 * Der Preis VOR dem Buchen (BUC-80).
 *
 * POST, obwohl nichts entsteht: Termin und Kunde gehoeren nicht in
 * Zugriffsprotokolle und Browserverlauf (LEG-13) — dieselbe Festlegung wie
 * fuer die Preisauskunft des Portals.
 */
export async function preisauskunft(eingabe: {
  readonly termin: string;
  readonly kunde: string;
  readonly plaetze: number;
}): Promise<Preisauskunft> {
  return rufe<Preisauskunft>('/buchungen/preisauskunft', { verfahren: 'POST', rumpf: eingabe });
}

/** BUC-45: rechnen, ohne einen Platz zu bewegen. */
export async function vorschau(
  buchung: string,
  art: Vorgangsart,
  eingabe: Omit<Vorgangseingabe, 'zielTermin' | 'grund'>,
): Promise<Vorschau> {
  return rufe<Vorschau>(`/buchungen/${buchung}/vorschau`, {
    verfahren: 'POST',
    rumpf: { art, ...eingabe },
  });
}

/**
 * Ausführen (BUC-142).
 *
 * DREI PFADE UND NICHT EIN SCHALTER. Am Server hängt an jedem ein eigenes
 * Recht; ein gemeinsamer Endpunkt mit der Art im Rumpf hätte nur eines davon
 * geprüft.
 */
export async function fuehreAus(
  buchung: string,
  art: Vorgangsart,
  eingabe: Vorgangseingabe,
): Promise<{ readonly vorgang: string; readonly vorschau: Vorschau }> {
  const pfad = art === 'storno' ? 'storno' : art === 'umbuchung' ? 'umbuchung' : 'teilnehmerwechsel';
  return rufe(`/buchungen/${buchung}/${pfad}`, { verfahren: 'POST', rumpf: eingabe });
}

/** Die Gebühr eines Vorgangs senken, solange kein Beleg erzeugt ist. */
export async function herabsetzen(vorgang: string, betrag: string, grund: string): Promise<void> {
  await rufe(`/buchungen/vorgaenge/${vorgang}/herabsetzen`, {
    verfahren: 'POST',
    rumpf: { betrag, grund },
  });
}

/** Einen Storno zurücknehmen; die Buchung steht danach wieder. */
export async function stornoZuruecknehmen(vorgang: string, grund: string): Promise<void> {
  await rufe(`/buchungen/vorgaenge/${vorgang}/ruecknahme`, { verfahren: 'POST', rumpf: { grund } });
}

export async function vorgaenge(buchung: string): Promise<readonly Vorgangszeile[]> {
  const antwort = await rufe<{ readonly vorgaenge: readonly Vorgangszeile[] }>(
    `/buchungen/${buchung}/vorgaenge`,
  );
  return antwort.vorgaenge;
}

/** Ein buchbarer Termin mit seiner freien Kapazität — die Auswahl (BUC-51). */
export type Terminauswahl = {
  readonly id: string;
  readonly artikel: string;
  readonly terminart: 'offen' | 'exklusiv';
  readonly beginn: string;
  readonly ende: string;
  readonly hoechstteilnehmer: number;
  readonly mindestteilnehmer: number;
  readonly status: string;
  /**
   * KAP-03: die maßgebliche freie Kapazität, das Minimum aus drei Grenzen —
   * nicht „Höchstteilnehmer minus Belegung". Ein Termin mit zwölf Plätzen und
   * einer Ressourcengrenze von sechs hat sechs.
   */
  readonly frei: number;
};

export type Teilnehmerzeile = {
  readonly id: string;
  readonly position: number;
  /** NULL heißt: Der Platz ist gebucht, der Name fehlt noch (BUC-70). */
  readonly name: string | null;
  readonly email: string | null;
  readonly geburtsdatum: string | null;
  readonly gemeldetAm: string | null;
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly buchungsstatus: string;
  readonly teilnehmerFrist: string | null;
  readonly kunde: string;
  readonly kundenname: string;
  /** Der Auftrag der Buchung — für den Weg zur Auftragsakte (CRM-117). */
  readonly auftrag: string | null;
  readonly auftragsnummer: string | null;
};

/** Buchbare Termine desselben Produkts (BUC-51). */
export async function buchbareTermine(
  artikel: string,
  ausser?: string,
): Promise<readonly Terminauswahl[]> {
  const frage = new URLSearchParams({ artikel });
  if (ausser !== undefined) frage.set('ausser', ausser);
  /*
   * `/termine` und nicht `/buchungen/termine`: Die Auskunft gehoert zum
   * Termin und wurde beim Bau des Terminreglers doppelt angeboten. Eine
   * Route je Sache — sonst driften die beiden beim ersten Filter auseinander.
   */
  const antwort = await rufe<{ readonly termine: readonly Terminauswahl[] }>(
    `/termine?${frage.toString()}`,
  );
  return antwort.termine;
}

/** Die Teilnehmerliste eines Termins (BUC-51, BUC-70). */
export async function teilnehmerliste(termin: string): Promise<readonly Teilnehmerzeile[]> {
  const antwort = await rufe<{ readonly teilnehmer: readonly Teilnehmerzeile[] }>(
    `/buchungen/termine/${termin}/teilnehmer`,
  );
  return antwort.teilnehmer;
}

/**
 * Den Beleg zu einem Vorgang erzeugen (BUC-145).
 *
 * Er entsteht als ENTWURF (RNG-03) — die Nummer kommt erst mit der
 * Festschreibung. Die `hinweise` nennen, was nicht auf dem Beleg steht und
 * von Hand zu ergänzen ist; sie dürfen nicht verschluckt werden.
 */
export async function belegErzeugen(
  vorgang: string,
): Promise<{ readonly id: string; readonly hinweise: readonly string[] }> {
  return rufe(`/rechnungen/aus-buchungsvorgang/${vorgang}`, { verfahren: 'POST' });
}

/** Ein Teilnehmerplatz — offen oder gemeldet (BUC-69, BUC-70). */
export type Teilnehmerstand = {
  readonly id: string;
  readonly position: number;
  /** NULL heißt: Der Platz ist gebucht, der Name fehlt noch. */
  readonly name: string | null;
  readonly email: string | null;
  readonly geburtsdatum: string | null;
  readonly gemeldetAm: string | null;
};

/** Eine Zeile der Arbeitsliste offener Teilnehmernamen (BUC-70). */
export type Offenerzeile = {
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly plaetze: number;
  readonly kundenname: string;
  readonly kunde: string;
  readonly teilnehmerFrist: string | null;
  readonly termin: string;
  readonly terminbeginn: string;
  readonly artikel: string;
  readonly offeneNamen: number;
  readonly fristAbgelaufen: boolean;
};

/**
 * Eine Buchung anlegen (BUC-50, BUC-51).
 *
 * `nachgebucht` ist BUC-51: „Nachbuchung durch Mitarbeiter ohne
 * Zahlungsstrecke." Sie ist ein Kennzeichen und kein eigener Weg — alles
 * andere ist gleich.
 */
export async function anlegen(eingabe: {
  termin: string;
  kunde: string;
  plaetze: number;
  /** B6: leer lassen — dann vergibt der Nummernkreis sie (BUC-50, BP-16). */
  buchungsnummer?: string;
  weg?: Buchungsweg;
  vertragsart?: string;
  auftrag?: string;
  teilnehmerFrist?: string;
  nachgebucht?: boolean;
  /* BUC-103: Besteller, Rechnungsempfänger und Teilnehmer sind drei Rollen. */
  rechnungsempfaenger?: string;
  /** BUC-127: der Ansprechpartner, der anmeldet (eine Kontaktrolle). */
  besteller?: string;
  /** BUC-80, BUC-127: die Namen, die beim Anlegen schon feststehen. */
  teilnehmer?: readonly { position: number; name: string; email?: string; geburtsdatum?: string }[];
  bestellreferenz?: string;
  kostenstelle?: string;
}): Promise<{
  readonly buchung: string;
  readonly haltung: string;
  /**
   * Die vergebene oder mitgebrachte Nummer (BUC-50, CRM-175) — die Maske
   * öffnet damit die neue Buchung. Wahlfrei: Ein Server vor dem 10.10.2026
   * nennt sie nicht.
   */
  readonly buchungsnummer?: string;
  /** Der Auftrag, der mit der Buchung entstanden ist (BUC-121, 16.09.2026). */
  readonly auftrag?: string;
  readonly auftragsnummer?: string;
}> {
  /*
   * ============================================================================
   * DIE BUCHUNG BRINGT IHREN AUFTRAG MIT (BUC-50, BUC-121)
   * ============================================================================
   *
   * Bis zum 16.09.2026 ging dieser Weg auf `POST /buchungen` — und legte eine
   * Buchung ohne Auftrag an. Gemessen im Bestand: 800 Buchungen, 0 mit
   * Auftrag, 0 Rechnungen an Privatkunden. Der Platzpreis steht am Auftrag;
   * ohne ihn kannte die Buchung ihren Preis nicht, und es gab weder Beleg
   * noch Stornogebuehr.
   *
   * `POST /buchungen/direkt` legt beides in EINER Klammer an. Der alte Weg
   * bleibt fuer den Fall, dass der Auftrag schon besteht — aus einem
   * angenommenen Angebot.
   */
  if (eingabe.auftrag !== undefined) {
    return rufe('/buchungen', { verfahren: 'POST', rumpf: eingabe });
  }
  const { auftrag: _weg, vertragsart: _art, ...rest } = eingabe;
  return rufe('/buchungen/direkt', { verfahren: 'POST', rumpf: rest });
}

/** Teilnehmernamen nachreichen (BUC-69). Eine abgelaufene Frist sperrt nicht. */
export async function teilnehmerMelden(
  buchung: string,
  teilnehmer: readonly { position: number; name: string; email?: string; geburtsdatum?: string }[],
): Promise<{ readonly gemeldet: number; readonly nachFristablauf: boolean }> {
  return rufe(`/buchungen/${buchung}/teilnehmer`, {
    verfahren: 'POST',
    rumpf: { teilnehmer },
  });
}

export async function teilnehmerZurBuchung(
  buchung: string,
): Promise<readonly Teilnehmerstand[]> {
  const antwort = await rufe<{ readonly teilnehmer: readonly Teilnehmerstand[] }>(
    `/buchungen/${buchung}/teilnehmer`,
  );
  return antwort.teilnehmer;
}

/** Die Arbeitsliste offener Teilnehmernamen (BUC-70) — nach Frist sortiert. */
export async function offeneTeilnehmer(vorlauf?: number): Promise<readonly Offenerzeile[]> {
  const p = vorlauf === undefined ? '' : `?vorlauf=${String(vorlauf)}`;
  const antwort = await rufe<{ readonly buchungen: readonly Offenerzeile[] }>(
    `/buchungen/offene-teilnehmer${p}`,
  );
  return antwort.buchungen;
}


// ===========================================================================
// Zusatzleistungen (BUC-119, BUC-86, BUC-89, PRD-26)
// ===========================================================================

export type Zusatzleistung = {
  readonly id: string;
  readonly teilnehmer: string | null;
  readonly teilnehmername: string | null;
  readonly position: number | null;
  readonly artikel: string;
  readonly bezeichnung: string;
  /** Steht am Artikel (BUC-84) und wird hier nicht gewählt, sondern befolgt. */
  readonly bezug: 'buchung' | 'teilnehmer';
  readonly menge: number;
  readonly einzelpreis: string | null;
  readonly steuerschluessel: string | null;
  readonly preisgrund: string | null;
  readonly nachgebucht: boolean;
  readonly status: 'gewaehlt' | 'storniert';
};

export type Einwand = {
  readonly art: 'setzt_voraus' | 'schliesst_aus' | 'ersetzt';
  readonly anderer: string;
  readonly andererName: string;
  readonly begruendung: string;
};

export type Verdraengt = {
  readonly id: string;
  readonly artikel: string;
  readonly bezeichnung: string;
  readonly begruendung: string;
};

export async function zusatzleistungen(
  buchung: string,
  auchStornierte = false,
): Promise<readonly Zusatzleistung[]> {
  const fragen = new URLSearchParams();
  if (auchStornierte) fragen.set('auchStornierte', 'true');
  const antwort = await rufe<{ readonly leistungen: readonly Zusatzleistung[] }>(
    `/buchungen/${buchung}/zusatzleistungen?${fragen.toString()}`,
  );
  return antwort.leistungen;
}

/**
 * Was eine Wahl bedeuten würde — OHNE sie zu treffen (BUC-86, FRS-15).
 *
 * Nicht anbieten, was ohnehin abgewiesen würde. Eine Wahl, deren Folgen man
 * erst hinterher erfährt, lässt sich nicht zurücknehmen, ohne dass eine
 * zweite Zeile im Protokoll steht.
 */
export async function zusatzleistungPruefen(
  buchung: string,
  artikel: string,
  teilnehmer: string | null,
): Promise<{
  readonly einwaende: readonly Einwand[];
  readonly verdraengt: readonly Verdraengt[];
}> {
  return rufe(`/buchungen/${buchung}/zusatzleistungen/pruefung`, {
    verfahren: 'POST',
    rumpf: { artikel, teilnehmer },
  });
}

export async function zusatzleistungWaehlen(
  buchung: string,
  eingabe: {
    readonly artikel: string;
    readonly teilnehmer: string | null;
    readonly menge?: number;
    readonly nachgebucht?: boolean;
  },
): Promise<{ readonly id: string; readonly verdraengt: readonly Verdraengt[] }> {
  return rufe(`/buchungen/${buchung}/zusatzleistungen`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

/**
 * Was zu dieser Buchung angeboten ist und wie viel zum Termin frei ist
 * (BUC-84, BUC-85 i. d. F. ÄA-31). `beschraenkt: false` heißt: Das Produkt
 * trägt noch kein Angebot, die Wahl ist frei.
 */
export type Zusatzangebotsstand = {
  readonly zusatz: string;
  readonly bezeichnung: string;
  readonly bezug: 'buchung' | 'teilnehmer';
  readonly auswahl: 'frei' | 'vorausgewaehlt' | 'verpflichtend';
  /** `null`: kein Pool, nicht knapp. */
  readonly frei: number | null;
};

/** AeA-31, Nachtrag: nach welcher Liste die Zusatzleistungen rechnen. */
export type Zusatzpreisliste = 'standard' | 'mitglied';

export async function zusatzangebotZurBuchung(buchung: string): Promise<{
  readonly beschraenkt: boolean;
  readonly angebote: readonly Zusatzangebotsstand[];
  readonly zusatzpreisliste: Zusatzpreisliste;
}> {
  return rufe(`/buchungen/${buchung}/zusatzleistungen/angebot`);
}

/**
 * Die Preisliste der Zusatzleistungen wählen (AeA-31, Nachtrag). Bis BUC-91 NOCH-OFFEN
 * wählt der Innendienst; schon gewählte Leistungen werden neu gepreist.
 */
export async function zusatzpreislisteSetzen(
  buchung: string,
  liste: Zusatzpreisliste,
): Promise<{ readonly neuGepreist: number }> {
  return rufe(`/buchungen/${buchung}/zusatzpreisliste`, { verfahren: 'PUT', rumpf: { liste } });
}

/** Stornieren, nicht löschen (BUC-89) — was berechnet werden kann, bleibt. */
export async function zusatzleistungStornieren(
  leistung: string,
): Promise<{ readonly storniert: true }> {
  return rufe(`/buchungen/zusatzleistungen/${leistung}/storno`, { verfahren: 'POST' });
}


// ===========================================================================
// Die Übersicht vor dem Abschluss (BUC-122)
// ===========================================================================

export type Uebersichtszeile = {
  readonly art: 'platz' | 'zusatzleistung';
  readonly bezeichnung: string;
  readonly menge: number;
  readonly einzelpreis: string | null;
  readonly gesamt: string | null;
  /** BUC-121: warum dieser Preis von der Regel abweicht. */
  readonly preisgrund: string | null;
};

export type Teilnehmerblock = {
  readonly teilnehmer: string;
  readonly position: number;
  readonly name: string | null;
  readonly zeilen: readonly Uebersichtszeile[];
  readonly summe: string;
  /** Release 2 (BUC-95 bis BUC-99) — trägt bis dahin immer `null`. */
  readonly mitgliedschaft: null;
};

export type Buchungsuebersicht = {
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly kundenart: string | null;
  readonly preisfuehrung: string | null;
  readonly teilnehmer: readonly Teilnehmerblock[];
  readonly fuerDieBuchung: readonly Uebersichtszeile[];
  readonly gesamtsumme: string;
  /** RNG-27 (AP-FA-03): bestätigte Eingänge minus Erstattungen, Wertgutscheine eingeschlossen. */
  readonly eingegangen: string;
  /** PRF-17: angerechnete BG-Gutscheine (offen, abgerechnet, bezahlt). */
  readonly angerechnet: string;
  /** RNG-27: was der Zahlungslink verlangt — vom Server gerechnet, nie unter 0,00. */
  readonly offen: string;
  /** Was die Summe NICHT enthält — offen benannt statt verschwiegen. */
  readonly vorbehalte: readonly string[];
};

/**
 * BUC-121: aus der Übersicht werden Auftragspositionen — eine je Teilnehmer.
 *
 * „Es entsteht ein Beleg an den Anmelder, in dem die Teilnehmer als
 * Einzelpositionen erscheinen." Sie lösen die Sammelposition ab, die seit dem
 * 16.09.2026 mit der Buchung entsteht; der Betrag steht danach nicht zweimal.
 */
export async function belegpositionenErzeugen(
  buchung: string,
): Promise<{ readonly auftrag: string; readonly positionen: number }> {
  return rufe(`/buchungen/${buchung}/belegpositionen`, { verfahren: 'POST' });
}

/**
 * WEB-12, BUC-37: einen Zahlungslink erzeugen.
 *
 * Der IDEMPOTENZSCHLÜSSEL kommt von hier und nicht vom Server (BUC-22). Ein
 * Schlüssel, den der Server erfände, wäre bei jedem Aufruf ein anderer — und
 * damit keiner. Er wird aus der Buchung und dem OFFENEN Betrag gebildet:
 * Zweimal derselbe Klick erzeugt dann keinen zweiten Vorgang; nach einer
 * Anzahlung ist der offene Betrag ein anderer und damit auch der Schlüssel.
 *
 * RNG-27 (AP-FA-03): Der Server prüft den Betrag gegen den offenen und weist
 * einen höheren ab (`zahlbetrag_ueber_offen`), ist nichts offen,
 * `buchung_bezahlt`.
 */
export async function zahlungslink(eingabe: {
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly offen: string;
  readonly beschreibung: string;
}): Promise<{
  readonly weiterleitung: string;
  readonly wiederholt: boolean;
  readonly vorgang: { readonly id: string; readonly status: string };
}> {
  return rufe('/zahlungen/zahlungslink', {
    verfahren: 'POST',
    rumpf: {
      betrag: eingabe.offen,
      idempotenzschluessel: `${eingabe.buchungsnummer}-${eingabe.offen}`,
      referenz: eingabe.buchungsnummer,
      beschreibung: eingabe.beschreibung,
      rueckkehrErfolg: `${window.location.origin}/zahlung/danke`,
      rueckkehrAbbruch: `${window.location.origin}/zahlung/abbruch`,
      buchung: eingabe.buchung,
    },
  });
}

export async function uebersicht(buchung: string): Promise<Buchungsuebersicht> {
  return rufe(`/buchungen/${buchung}/uebersicht`);
}
